import { ColDef } from '@ag-grid-community/core';
import { ICONS } from '@creditsights/styleguide-web';
import { PermissionEnum } from '@creditsights/api-ui-web';
import { RpColumn, RpColumnRenderType } from '../../models/RpColumn';
import { IssuerListItemDto } from '../../services/dtos/issuer/issuerList.dto';
import { EsField } from '../../services/dtos/riskProducts/rpColumn.dto';

export const additionalDefaultColDef: ColDef<IssuerListItemDto> = {
  comparator: () => 0, // Prevent sort trigger before data loads when using client side data model
};
export const COLUMN_PERSISTENCE_KEY = 'companies-table-columns';
export const GRID_PAGE_SIZE = 100;

export const DEFAULT_SORT_FIELD = 'name';
export const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'asc';

export const EXCLUDED_GROUP = 'Company Details';
export const EXCLUDED_COMPANY_ES_FIELDS = [
  'rating_stability_indicator',
  'potential_rating_action',
  'cre_previous',
  'cqs_previous',
  'fscore_previous',
  'credit_risk_level_previous',
  'acr_previous',
];

export const EXCLUDED_USERS_COLUMN_GROUP_HEADERS = ['CR', 'LFI', 'CR_LFI'];
export const RISK_PRODUCT_PREFIX = 'riskProducts';
export const COVERAGE_ANDOR_PERMISSION = 'ui:company_search_andor';

export const WEIGHTING_ICONS_MAP = {
  buy: ICONS.Buy,
  hold: ICONS.Hold,
  marketperform: ICONS.Marketperform,
  norecommendation: ICONS.Norecommendation,
  outperform: ICONS.Outperform,
  sell: ICONS.Sell,
  underperform: ICONS.Underperform,
};

export type DefaultFieldPermission =
  | PermissionEnum.RESEARCH
  | PermissionEnum.COVENANTS
  | PermissionEnum.NEWS
  | PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_2
  | PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3;

export type DefaultCompanyColumn = {
  fieldName: EsField;
  permissions?: DefaultFieldPermission[];
  allPermissions?: boolean;
};

export const DEFAULT_COMPANY_COLUMNS: DefaultCompanyColumn[] = [
  { fieldName: 'selection', allPermissions: true },
  { fieldName: 'name', allPermissions: true },
  { fieldName: 'ticker', allPermissions: true },
  { fieldName: 'coverage', allPermissions: true },
  { fieldName: 'sector', permissions: [PermissionEnum.RESEARCH, PermissionEnum.COVENANTS, PermissionEnum.NEWS] },
  { fieldName: 'currency', permissions: [PermissionEnum.RESEARCH, PermissionEnum.COVENANTS, PermissionEnum.NEWS] },
  {
    fieldName: 'creditQualities',
    permissions: [PermissionEnum.RESEARCH, PermissionEnum.COVENANTS, PermissionEnum.NEWS],
  },
  {
    fieldName: 'topLevelRegion',
    permissions: [PermissionEnum.RESEARCH, PermissionEnum.COVENANTS, PermissionEnum.NEWS],
  },
  { fieldName: 'country', permissions: [PermissionEnum.RESEARCH, PermissionEnum.COVENANTS, PermissionEnum.NEWS] },
  { fieldName: 'latestRecommendation.weighting', permissions: [PermissionEnum.RESEARCH] },
  { fieldName: 'latestRecommendation.changedDate', permissions: [PermissionEnum.RESEARCH] },
  { fieldName: 'latestRecommendation.publishDate', permissions: [PermissionEnum.RESEARCH] },
  { fieldName: 'coreScores.assessment', allPermissions: true },
  { fieldName: 'coreScores.outlook', allPermissions: true },
  { fieldName: 'coreScores.reviewedAt', allPermissions: true },
  {
    fieldName: 'riskProducts.model_sector',
    permissions: [PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_2, PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3],
  },
  {
    fieldName: 'riskProducts.cqs',
    permissions: [PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_2, PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3],
  },
  {
    fieldName: 'riskProducts.cqs_trend_1y',
    permissions: [PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_2, PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3],
  },
  {
    fieldName: 'riskProducts.cqs_trend_3y',
    permissions: [PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_2, PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3],
  },
  {
    fieldName: 'riskProducts.cqs_outlook',
    permissions: [PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_2, PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3],
  },
  {
    fieldName: 'riskProducts.acr_level_4',
    permissions: [PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_2, PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3],
  },
  {
    fieldName: 'riskProducts.cre_1y',
    permissions: [PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3],
  },
  {
    fieldName: 'riskProducts.credit_risk_level',
    permissions: [PermissionEnum.RISK_PRODUCTS_ENTITLEMENT_LEVEL_3],
  },
];

export const CORE_SCORE_COLUMNS: RpColumn[] = [
  {
    group: 'CoreScore',
    data_field: 'CoreScore',
    es_field: 'coreScores.assessment',
    renderType: RpColumnRenderType.CORE_SCORE,
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'CoreScore Outlook',
    es_field: 'coreScores.outlook',
    renderType: RpColumnRenderType.CORE_SCORE_OUTLOOK,
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'CoreScore Reviewed',
    es_field: 'coreScores.reviewedAt',
    renderType: RpColumnRenderType.DATE,
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'CoreScore Changed',
    es_field: 'coreScores.changedAt',
    renderType: RpColumnRenderType.DATE,
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Sector Risk Score',
    es_field: 'coreScores.sector',
    renderType: RpColumnRenderType.SCORE,
    colId: 'sectorAssessment',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Sector Risk Outlook',
    es_field: 'coreScores.sector',
    renderType: RpColumnRenderType.SCORE,
    colId: 'sectorOutlook',
    canAccess: true,
  },

  {
    group: 'CoreScore',
    data_field: 'Event Risk Score',
    es_field: 'coreScores.event',
    renderType: RpColumnRenderType.SCORE,
    colId: 'eventAssessment',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Event Risk Outlook',
    es_field: 'coreScores.event',
    renderType: RpColumnRenderType.SCORE,
    colId: 'eventOutlook',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Cash Flow Score',
    es_field: 'coreScores.cash_flow',
    renderType: RpColumnRenderType.SCORE,
    colId: 'cashAssessment',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Cash Flow Outlook',
    es_field: 'coreScores.cash_flow',
    renderType: RpColumnRenderType.SCORE,
    colId: 'cashOutlook',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Leverage Score',
    es_field: 'coreScores.leverage',
    renderType: RpColumnRenderType.SCORE,
    colId: 'leverageAssessment',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Leverage Outlook',
    es_field: 'coreScores.leverage',
    renderType: RpColumnRenderType.SCORE,
    colId: 'leverageOutlook',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Liquidity Score',
    es_field: 'coreScores.liquidity',
    renderType: RpColumnRenderType.SCORE,
    colId: 'liquidityAssessment',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Liquidity Outlook',
    es_field: 'coreScores.liquidity',
    renderType: RpColumnRenderType.SCORE,
    colId: 'liquidityOutlook',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Profitability Score',
    es_field: 'coreScores.profitability',
    renderType: RpColumnRenderType.SCORE,
    colId: 'profitabilityAssessment',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Profitability Outlook',
    es_field: 'coreScores.profitability',
    renderType: RpColumnRenderType.SCORE,
    colId: 'profitabilityOutlook',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Capital Score',
    es_field: 'coreScores.capital',
    renderType: RpColumnRenderType.SCORE,
    colId: 'capitalAssessment',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Capital Outlook',
    es_field: 'coreScores.capital',
    renderType: RpColumnRenderType.SCORE,
    colId: 'capitalOutlook',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Asset Quality Scores',
    es_field: 'coreScores.asset_quality',
    renderType: RpColumnRenderType.SCORE,
    colId: 'assetQualityAssessment',
    canAccess: true,
  },
  {
    group: 'CoreScore',
    data_field: 'Asset Quality Outlook',
    es_field: 'coreScores.asset_quality',
    renderType: RpColumnRenderType.SCORE,
    colId: 'assetQualityOutlook',
    canAccess: true,
  },
];

export const CORE_SCORE_COL_IDS: string[] = CORE_SCORE_COLUMNS.map((item) => (item.colId ? item.colId : item.es_field));

export const COMPANY_DETAILS_COLUMNS: RpColumn[] = [
  {
    group: 'Company Details',
    data_field: '',
    es_field: 'selection',
    renderType: RpColumnRenderType.SELECTION,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Issuer/Borrower',
    colId: 'name',
    es_field: 'displayName',
    renderType: RpColumnRenderType.COMPANY_LINK,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'CS View',
    colId: 'latestRecommendation.weighting',
    es_field: 'latestRec.weighting',
    renderType: RpColumnRenderType.CS_VIEW,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Ticker',
    colId: 'ticker',
    es_field: 'displayTicker',
    renderType: RpColumnRenderType.DEFAULT,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Rec Changed',
    colId: 'latestRecommendation.changedDate',
    es_field: 'latestRec.changedDate',
    renderType: RpColumnRenderType.DATE,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Rec Reviewed',
    colId: 'latestRecommendation.publishDate',
    es_field: 'latestRec.publishDate',
    renderType: RpColumnRenderType.DATE,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Sector/Subsector',
    es_field: 'sector',
    renderType: RpColumnRenderType.IMPLIED,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Currency',
    es_field: 'currency',
    renderType: RpColumnRenderType.IMPLIED,
    meta: { sortable: false },
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Credit Grade',
    es_field: 'creditQualities',
    renderType: RpColumnRenderType.CREDIT,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Region',
    colId: 'topLevelRegion',
    es_field: 'region',
    renderType: RpColumnRenderType.IMPLIED,
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Country or Territory',
    es_field: 'country',
    renderType: RpColumnRenderType.IMPLIED,
    meta: { sortable: false },
    canAccess: true,
  },
  {
    group: 'Company Details',
    data_field: 'Coverage',
    es_field: 'coverage',
    renderType: RpColumnRenderType.COVERAGE,
    canAccess: true,
  },
];

export const CORE_SCORE_COLUMN_LABELS: string[] = [
  'CoreScore',
  'CoreScore Outlook',
  'CoreScore Reviewed',
  'CoreScore Changed',
  'Sector Risk Score',
  'Sector Risk Outlook',
  'Event Risk Score',
  'Event Risk Outlook',
  'Cash Flow Score',
  'Cash Flow Outlook',
  'Leverage Score',
  'Leverage Outlook',
  'Liquidity Score',
  'Liquidity Outlook',
  'Profitability Score',
  'Profitability Outlook',
  'Capital Score',
  'Capital Outlook',
  'Asset Quality Scores',
  'Asset Quality Outlook',
];
